import { apiClient, ApiError } from "@nabble/sdk";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext";
import { destinationAfterStep, type StepResponse } from "../../../auth/session";
import { getPasskey } from "../../../auth/webauthn";
import { passkeyErrorMessage } from "../../../components/passkeyErrorMessage";
import { errorMessage } from "../../../errors";
import { type OtpCode, useOtpCode } from "../../../hooks/useOtpCode";

const TOTP_PATH = "/api/auth/mfa/totp";
const RECOVERY_PATH = "/api/auth/mfa/recovery";

const PASSKEY_ERRORS = { server: "We couldn't verify the passkey.", browser: "We couldn't use the passkey. If you canceled, try again." };

function codeErrorMessage(err: unknown): string {
  return err instanceof ApiError && err.code === "invalid_code"
    ? "That code isn't valid. Try the next one your app shows."
    : errorMessage(err, "We couldn't verify the code.");
}

export interface MfaVerification {
  totp: OtpCode;
  recovery: string;
  setRecovery: (value: string) => void;
  loading: boolean;
  error: string | null;
  clearError: () => void;
  verifyTotp: (code: string) => Promise<void>;
  verifyRecovery: () => Promise<void>;
  verifyPasskey: () => Promise<void>;
}

/** The second sign-in step: checks a code or a passkey and moves on to the next step. */
export function useMfaVerification(): MfaVerification {
  const navigate = useNavigate();
  const { applyNext } = useAuth();
  const totp = useOtpCode();
  const [recovery, setRecovery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitting = useRef(false);

  function goToNext({ next, rememberedUntil }: StepResponse): void {
    applyNext(next, rememberedUntil);
    navigate(destinationAfterStep(next));
  }

  async function submitCode(path: string, value: string): Promise<void> {
    if (submitting.current) return;
    submitting.current = true;
    setLoading(true);
    setError(null);
    try {
      goToNext(await apiClient<StepResponse>(path, { method: "POST", body: { code: value } }));
    } catch (err) {
      setError(codeErrorMessage(err));
      totp.reject();
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  }

  async function verifyPasskey(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const options = await apiClient("/api/auth/mfa/passkey/options", { method: "POST" });
      const credential = await getPasskey(options as Record<string, unknown>);
      goToNext(await apiClient<StepResponse>("/api/auth/mfa/passkey", { method: "POST", body: { credential } }));
    } catch (err) {
      setError(passkeyErrorMessage(err, PASSKEY_ERRORS));
    } finally {
      setLoading(false);
    }
  }

  return {
    totp,
    recovery,
    setRecovery,
    loading,
    error,
    clearError: () => setError(null),
    verifyTotp: (code) => submitCode(TOTP_PATH, code),
    verifyRecovery: () => submitCode(RECOVERY_PATH, recovery.trim()),
    verifyPasskey,
  };
}
